import { format, differenceInMinutes, differenceInHours, differenceInDays, differenceInYears } from 'date-fns'

/**
 * Smart timestamp formatting with progressive detail levels
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted timestamp string
 * 
 * Rules:
 * - < 60 minutes: "15m ago" (relative minutes)
 * - 1-24 hours: "9:32 PM" (time of day)  
 * - 1-7 days: "Sun", "Mon" (day abbreviation)
 * - 1 week - 1 year: "Sep 25" (month + day)
 * - > 1 year: "9/25/25" (short date)
 */
export function formatSmartTimestamp(dateString: string | Date): string {
	const now = new Date()
	const date = typeof dateString === 'string' ? new Date(dateString) : dateString
	
	const minutesAgo = differenceInMinutes(now, date)
	const hoursAgo = differenceInHours(now, date)
	const daysAgo = differenceInDays(now, date)
	const yearsAgo = differenceInYears(now, date)
	
	if (minutesAgo < 60) {
		// Less than 60 minutes: "N minutes ago"
		return `${minutesAgo}m ago`
	} else if (hoursAgo < 24) {
		// Less than 24 hours: "H:mm a" like "9:32 PM"
		return format(date, 'h:mm a')
	} else if (daysAgo < 7) {
		// Less than 7 days: day abbreviation like "Sun", "Mon"
		return format(date, 'EEE')
	} else if (yearsAgo < 1) {
		// Less than 1 year: "MMM d" like "Sep 25"
		return format(date, 'MMM d')
	} else {
		// More than 1 year: "M/d/yy" like "9/25/25"
		return format(date, 'M/d/yy')
	}
}

/**
 * Alternative timestamp formatting for different contexts
 * 
 * @param dateString - ISO date string or Date object
 * @param style - 'compact' | 'detailed' | 'relative'
 * @returns Formatted timestamp string
 */
export function formatTimestamp(dateString: string | Date, style: 'compact' | 'detailed' | 'relative' = 'compact'): string {
	const date = typeof dateString === 'string' ? new Date(dateString) : dateString
	
	switch (style) {
		case 'compact':
			return formatSmartTimestamp(date)
		case 'detailed':
			return format(date, 'MMM d, yyyy h:mm a')
		case 'relative':
			return format(date, 'MMM d')
		default:
			return formatSmartTimestamp(date)
	}
}
